// Route Code Edited 07/10/2026
/**
 *  Route Add-to-Cart Checkout Sync Handler (with CWC)
 * ===================================================================
 * Some cart apps disable the original checkout button during cart updates,
 * blocking Route's redirect. We patch fetch early to detect Route's cart
 * API calls, then wait for the button to re-enable before clicking it.
 *
 * Handles both the Route checkout button (add.js) and the CWC Button (update.js).
 * 
 * Load without defer/async before </body> in theme.liquid:
 * <!-- Route Code Edited 07/__/2026 -->
 * <script src="{{ 'route-checkout-sync-handler.js' | asset_url }}"></script>
 * <!-- End Route Code -->
 *
 */

// Shared between Part 1 and Part 2 — skip processing until a Route button is clicked
let checkoutInitiated = false;

// ─── PART 1: Patch fetch before any other scripts can wrap it ────────────────
(function () {
  const _fetch = window.fetch;
  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
    const reqBody = args[1]?.body;
    const promise = _fetch.apply(this, args);

    if (url.includes('cart/add.js')) {
      promise.then(async (res) => {
        if (!checkoutInitiated) return;
        let body = null;
        try { body = await res.clone().json(); } catch (e) {}
        window.dispatchEvent(new CustomEvent('route:fetch:end', { detail: { url, body } }));
      });
    } else if (url.includes('cart/update.js')) {
      promise.then(() => {
        // CWC button: confirmed as Route via request body containing route_toggle_state
        if (checkoutInitiated && typeof reqBody === 'string' && reqBody.includes('route_toggle_state')) {
          window.dispatchEvent(new CustomEvent('route:fetch:end', { detail: { url, body: null } }));
        }
      });
    }
    return promise;
  };
})();

// ─── PART 2: Monitor Route button clicks and wait for the right moment ─────
document.addEventListener('DOMContentLoaded', function () {
  const ROUTE_BTN = '[data-route-cloned-button]';
  const CWC_BTN = '.route-continue-without-coverage-link';
  const UPDATE_JS_URL = 'cart/update.js';
  const ROUTE_HANDLE = 'routeins';
  const MAX_MS = 5000;

  // Read the original checkout button selector from the Route widget script URL params
  const s = document.querySelector('script[src*="shopify.widget.js"][src*="entrypoints"]');
  const ORIGINAL_BTN_BASE = (s ? new URL(s.src).searchParams.get('entrypoints[0].checkoutSelector') : null) ?? '[data-original-display]';
  // Apply :not() exclusions to each selector individually to handle comma-separated lists
  const ORIGINAL_BTN = ORIGINAL_BTN_BASE.split(',')
    .map(sel => `${sel.trim()}:not([data-route-widget] *):not([data-route-cloned-button])`)
    .join(', ');

  // Reset on bfcache restore to prevent immediate redirect on back navigation
  window.addEventListener('pageshow', (e) => { if (e.persisted) checkoutInitiated = false; });

  document.addEventListener('click', function (e) {
    const routeBtn = e.target.closest(ROUTE_BTN);
    const cwcBtn = e.target.closest(CWC_BTN);
    if ((!routeBtn && !cwcBtn) || checkoutInitiated) return;
    checkoutInitiated = true;

    const clickedBtn = routeBtn ?? cwcBtn;
    const isCwc = !!cwcBtn;

    // Supports both Paired and Standard layout
    const container = (clickedBtn.closest('[data-route-widget]') ?? clickedBtn.closest(isCwc ? '[data-route-widget-cwc]' : '[data-route-widget-button]'))?.parentElement;
    const originalBtn = container?.querySelector(ORIGINAL_BTN) ?? document.querySelector(ORIGINAL_BTN);
    if (!originalBtn) { checkoutInitiated = false; return; }

    const maxTimer = setTimeout(() => {
      window.removeEventListener('route:fetch:end', onFetchEnd);
      enableObserver.disconnect();
      checkoutInitiated = false;
      originalBtn.click();
    }, MAX_MS);

    // Wait for the button to re-enable after the cart app finishes its update cycle
    const enableObserver = new MutationObserver(() => {
      if (!originalBtn.disabled) {
        enableObserver.disconnect();
        clearTimeout(maxTimer);
        originalBtn.click();
      }
    });

    // Confirm the fetch is the right Route call, then start observing the button
    function onFetchEnd(e) {
      const url = e.detail.url;
      if (!isCwc) {
        if (!url.includes('cart/add.js')) return;
        if (!e.detail.body?.items?.some(i => i.handle === ROUTE_HANDLE)) return;
      } else {
        if (!url.includes(UPDATE_JS_URL)) return;
      }

      window.removeEventListener('route:fetch:end', onFetchEnd);

      // CWC may not always disable the button — click immediately if already enabled
      if (!originalBtn.disabled) {
        enableObserver.disconnect();
        clearTimeout(maxTimer);
        originalBtn.click();
        return;
      }
      enableObserver.observe(originalBtn, { attributes: true, attributeFilter: ['disabled'] });
    }
    window.addEventListener('route:fetch:end', onFetchEnd);
  }, true);
});